import { Fragment, useCallback, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "react-bootstrap";
import Icon from "../controls/Icon";
import useNavbar from "../../common/hooks/NavbarHooks";
import { useHasAnyPermission } from "../../common/hooks/Hooks";
import UI_PERMISSIONS_NAME from "../../config/uiPermissionsNames";
import Controls from "./Controls";
import CONFIG from "../../config/config";
import PaginationControls from "../controls/PaginationControls";
import ListStatus from "../controls/ListStatus";
import { useAppContext } from "../../common/ApplicationServicesContext";

const VerticalNavbar = ({
    onSelectItem,
}) => {
    const [translate] = useTranslation();
    const { id, objectSubType } = useParams();
    const { pathname } = useLocation();
    const objectType = pathname.split("/")[1] || undefined;
    const {
        handleDomainManagementClick,
        handleDeploymentClick,
        handleDrillDownClick,
        handleNestedDrillDownClick,
        selectedTab,
        deployments,
        drillDownObjects,
        next,
        previous,
        hasNextPage,
        currentPage,
        isUnlocked,
        handleUnlockClick,
        confirmationPrompt,
        unlockTimerToast,
        deploymentEditPermission,
        isLoading
    } = useNavbar();
    const { isRootDomain } = useAppContext();

    const viewDomainPermission = useHasAnyPermission({
        names: [UI_PERMISSIONS_NAME.ViewDomain, UI_PERMISSIONS_NAME.EditDomain]
    });

    const renderNestedDrillDownObject = useCallback((parentEl, element, index, addOnSelectItem) => {
        const isActive = objectSubType === element.id;
        return element.visible &&
            <button
                key={index}
                className={`nav-item quick-nav-link nested text-end ps-4 pe-3 py-1 ${isActive ? "active" : ""}`}
                onClick={() => {
                    if (addOnSelectItem) {
                        onSelectItem();
                    }
                    handleNestedDrillDownClick(parentEl.id, selectedTab._id, element.urlPath);
                }}
            >
                {element.name}
            </button>
    }, [selectedTab, onSelectItem, objectSubType, handleNestedDrillDownClick]);

    const verticalDrillDownHeader = useCallback(({ addOnSelectItem = false } = {}) => {
        if (!selectedTab) {
            return <></>
        }
        return (
            <div className="nav flex-column">
                {drillDownObjects.map((element) => (
                    element.visible &&
                    <Fragment key={element.id}>
                        <button
                            className={`nav-item quick-nav-link py-1 ${objectType === element.id ? "active" : ""}`}
                            onClick={() => {
                                if (addOnSelectItem && !element.nestedViews) {
                                    onSelectItem();
                                }
                                handleDrillDownClick(element.id, selectedTab._id, element.nestedViews);
                            }}
                        >
                            <div className="d-flex align-items-center">
                                <Icon icon="ChevronRight" className="me-2 small" />
                                <span>{element.name}</span>
                            </div>
                        </button>
                        {element.nestedViews && objectType === element.id &&
                            <div className="nested-items-container">
                                {element.nestedViews.map((nestedEl, nestedIndex) => renderNestedDrillDownObject(element, nestedEl, nestedIndex, addOnSelectItem))}
                            </div>
                        }
                    </Fragment>
                ))}
            </div>
        );
    }, [selectedTab, drillDownObjects, onSelectItem, objectType, handleDrillDownClick, renderNestedDrillDownObject]);

    const rootDomainVerticalHeader = useMemo(() => {
        return (
            <div className="nav flex-column">
                {drillDownObjects.map((element) => (
                    element.visible &&
                    <button key={element.id}
                        className={`nav-item nav-link py-1 ${objectType === element.id ? "active fw-bold" : ""}`}
                        onClick={() => {
                            handleDrillDownClick(element.id, selectedTab._id);
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <span>{element.name}</span>
                        </div>
                    </button>
                ))}
            </div>
        );
    }, [drillDownObjects, handleDrillDownClick, objectType, selectedTab])

    const verticalHeader = useMemo(() => {
        const activeKey = typeof selectedTab === 'string' ? selectedTab : selectedTab?._id;

        return (<>
            {viewDomainPermission && (
                <div className="mb-2">
                    <div className="px-3 py-1 text-muted small text-uppercase fw-bold">
                        {translate("OTM_DOMAIN")}
                    </div>
                    <div className="nav flex-column">
                        <button
                            className={`nav-item nav-link py-1 ${selectedTab === "domain-management" ? "active fw-bold" : ""}`}
                            onClick={() => handleDomainManagementClick()}
                        >
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0 me-2">
                                    <Icon icon="Settings" className="fs-6" />
                                </div>
                                <span className="flex-grow-1 text-start">{translate("OTM_DOMAIN_MANAGEMENT")}</span>
                            </div>
                        </button>
                    </div>
                </div>
            )}

            <div className="mb-2">
                <div className="px-3 py-1 text-muted small text-uppercase fw-bold">
                    {translate("OTM_DEPLOYMENTS")}
                </div>
                {isLoading && <div className="px-3">
                    <ListStatus loading={isLoading} empty={true} />
                </div>}

                {!isLoading && <>
                    <div className="nav flex-column deployment-list">
                        {deployments.map((deployment) => {
                            const showLockIcon = CONFIG.LOCKED_STAGES.includes(deployment.environment?.stage);
                            const isActive = selectedTab?._id === deployment._id;
                            const locked = !isUnlocked(deployment._id);

                            return (
                                <button
                                    key={deployment._id}
                                    className={`nav-item nav-link py-1 position-relative ${isActive ? "active fw-bold" : ""}`}
                                    onClick={() => handleDeploymentClick(deployment)}
                                >
                                    <div className="d-flex align-items-start">
                                        <div className="flex-shrink-0 me-2">
                                            {showLockIcon ? <Icon
                                                icon={locked ? "LockOutlined" : "LockOpen"}
                                                className={
                                                    [
                                                        "lockIcon fs-6",
                                                        activeKey === deployment._id && deploymentEditPermission[deployment._id] ? "highlight" : "",
                                                        locked ? "text-danger" : "text-success",
                                                    ].filter(Boolean).join(" ")
                                                }
                                                onClick={(e) => {
                                                    if (activeKey === deployment._id && deploymentEditPermission[deployment._id]) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleUnlockClick(deployment);
                                                    }
                                                }}
                                            /> : <Icon icon="Layers" className="fs-6" />}
                                        </div>
                                        <span className="flex-grow-1 text-start" style={{ wordBreak: 'break-word' }}>
                                            {deployment.name}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="px-3 mt-1 d-flex align-items-center justify-content-end">
                        <PaginationControls
                            previous={previous}
                            next={next}
                            hasNextPage={hasNextPage}
                            currentPage={currentPage}
                            totalPages={hasNextPage ? currentPage + 1 : currentPage}
                        />
                    </div>
                </>}
            </div>

            {((selectedTab?._id === id) || (selectedTab === "domain-management")) && drillDownObjects.some(obj => obj.visible) && (
                <div className="mb-2">
                    <div className="px-3 py-1 text-muted small text-uppercase fw-bold">
                        {translate("OTM_NAVIGATION")}
                    </div>
                    {verticalDrillDownHeader({ addOnSelectItem: true })}
                </div>
            )}
        </>
        );
    }, [translate, deployments, isLoading, next, previous, hasNextPage, currentPage, handleDomainManagementClick, handleDeploymentClick,
        id, selectedTab, viewDomainPermission, verticalDrillDownHeader, handleUnlockClick, isUnlocked, deploymentEditPermission, drillDownObjects]);

    return <>
        {confirmationPrompt}
        {unlockTimerToast}

        <div className="vertical-navbar">
            <div className="vertical-navbar-scrollable">
                <Card className="shadow-sm border-0">
                    <Card.Body className="p-0">
                        {isRootDomain ? (
                            <div className="mb-2">
                                <div className="px-3 py-1 text-muted small text-uppercase fw-bold">
                                    {translate("OTM_NAVIGATION")}
                                </div>
                                {rootDomainVerticalHeader}
                            </div>
                        ) : (
                            verticalHeader
                        )}
                    </Card.Body>
                </Card>
            </div>

            <Card className="mt-3 shadow-sm border-0">
                <Card.Body>
                    <Controls fromVertical={true} />
                </Card.Body>
            </Card>
        </div>
    </>
};

export default VerticalNavbar;
